// src/admin/components/BuyLinksEditor.jsx
//
// Ticket 118. "Edit buy links" on a book row: lists every link the book has, from both places,
// says which ones readers see and why not when they don't, adds a link, changes or takes off a
// link in the book's own list, and removes a link from the import table.
//
// Everything it reads and writes is in ../lib/buyLinks.js. Wording is the approved wording
// (LINK_WORDS there), word for word. It opens under the row, the way the tag editor does.

import { useState, useEffect, useCallback } from 'react';
import { BUY_STORES, LINK_WORDS, describeLinks, newLinkProblem, readBookLinks, saveColumnLinks, removeTableLink, addressKey, trimmed, linksDiffer } from '../lib/buyLinks';
import { setUnsaved } from '../../lib/unsavedChanges';


const fieldStyle = {
  padding: '7px 10px', fontSize: 'var(--font-size-sm)', border: '1px solid var(--brand-sand)',
  borderRadius: 'var(--radius-md)', outline: 'none', fontFamily: 'var(--font-body)', background: 'var(--brand-white)',
  boxSizing: 'border-box',
};
const buttonStyle = (primary, disabled) => ({
  fontSize: 11.5, fontWeight: 600, padding: '6px 13px', borderRadius: 'var(--radius-md)', whiteSpace: 'nowrap',
  fontFamily: 'var(--font-body)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  background: primary ? 'var(--neutral-800)' : 'var(--brand-white)',
  color: primary ? 'var(--brand-white)' : 'var(--neutral-600)',
  border: primary ? '1px solid var(--neutral-800)' : '1px solid var(--brand-sand)',
});

// `onDirtyChange(true|false)` tells the row when there are changes that are not saved, so its own
// "Close buy links" button can ask before it drops them.
export default function BuyLinksEditor({ bookId, title, onClose, onDirtyChange }) {
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState(null);
  const [tableLinks, setTableLinks]   = useState([]);
  const [savedColumn, setSavedColumn] = useState([]);   // the book's own links as last read or saved
  const [openedWith, setOpenedWith]   = useState([]);   // the column exactly as it was read, to spot a change underneath
  const [draft, setDraft]             = useState([]);   // the book's own links as shown, edited here until Save
  const [store, setStore]             = useState(BUY_STORES[0]);
  const [address, setAddress]         = useState('');
  const [message, setMessage]         = useState(null); // { tone: 'ok' | 'bad', text }
  const [busy, setBusy]               = useState(false);

  const load = useCallback(async () => {
    const res = await readBookLinks(bookId);
    if (res.error) { setLoadError(res.error); setLoading(false); return false; }
    setLoadError(null);
    setTableLinks(res.tableLinks);
    setSavedColumn(res.column);
    setDraft(res.column);
    setOpenedWith(res.rawColumn);
    setLoading(false);
    return true;
  }, [bookId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // Changed means what the audit line would say: a link that went on or came off, by store and trimmed
  // address. A space on the end of an address, or a link taken off and put back, is not a change.
  const norm = list => list.map(trimmed);
  const dirty = linksDiffer(draft, savedColumn);
  const rows = describeLinks(tableLinks, draft);

  // The screen's other ways off (tabs, pages, the sidebar, the top bar, Back, closing the tab) read the
  // same flag as the row's button (src/lib/unsavedChanges.js), so every one of them asks the same question.
  useEffect(() => { onDirtyChange?.(dirty); setUnsaved(dirty, LINK_WORDS.closeAnyway); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { onDirtyChange?.(false); setUnsaved(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A link is new when the saved list has no link with that store and address. A link changed here
  // is new too, until Save.
  const had = new Set(savedColumn.map(l => `${l.retailer} ${addressKey(l.url)}`));
  const isNew = l => !had.has(`${l.retailer} ${addressKey(l.url)}`);

  function close() {
    if (dirty && !window.confirm(LINK_WORDS.closeAnyway)) return;
    onClose();
  }

  function add() {
    const link = { retailer: store, url: address.trim() };
    if (!link.url) return;
    const problem = newLinkProblem(link, [...tableLinks, ...draft]);
    // A link that can't be added says why and keeps what was typed.
    if (problem) { setMessage({ tone: 'bad', text: problem }); return; }
    setDraft(d => [...d, link]);
    setAddress('');
    setMessage(null);
  }

  function change(index, patch) {
    setDraft(d => d.map((l, i) => (i === index ? { ...l, ...patch } : l)));
    setMessage(null);
  }

  function takeOff(index) {
    setDraft(d => d.filter((_, i) => i !== index));
    setMessage(null);
  }

  async function save() {
    // A link that is new or changed has to be a page for the book and not a copy of an address the
    // book already has. A link that was already there stays as it is, even if readers don't see it.
    for (let i = 0; i < draft.length; i++) {
      const l = draft[i];
      if (!isNew(l)) continue;
      const others = [...tableLinks, ...draft.filter((_, j) => j !== i)];
      const problem = newLinkProblem({ ...l, url: String(l.url ?? '').trim() }, others);
      if (problem) { setMessage({ tone: 'bad', text: problem }); return; }
    }
    setBusy(true);
    const res = await saveColumnLinks({ bookId, title, openedWith, draft: norm(draft) });
    if (res.status === 'saved') {
      setSavedColumn(res.column);
      setDraft(res.column);
      setOpenedWith(res.rawColumn);
      setMessage({ tone: 'ok', text: LINK_WORDS.saved });
    } else if (res.status === 'stale') {
      // Nothing was written. Show the links as they are now, the import table's too.
      await load();
      setMessage({ tone: 'bad', text: LINK_WORDS.changedUnderneath });
    } else {
      setMessage({ tone: 'bad', text: LINK_WORDS.notSaved(res.error) });
    }
    setBusy(false);
  }

  async function removeFromTable(link) {
    if (!window.confirm(LINK_WORDS.removeConfirm)) return;
    setBusy(true);
    const res = await removeTableLink({ bookId, title, link });
    setBusy(false);
    if (res.error) { setMessage({ tone: 'bad', text: LINK_WORDS.notRemoved(res.error) }); return; }
    setTableLinks(ts => ts.filter(t => t.id !== link.id));
    setMessage(null);
  }

  if (loading) return <div style={{ padding: '12px 14px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>Loading buy links...</div>;

  let columnIndex = -1;
  return (
    <div data-buy-links-editor={bookId} style={{ marginTop: 'var(--space-md)', padding: '14px 16px', background: 'var(--brand-paper)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-10)' }}>
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)' }}>Edit buy links</span>
        <button onClick={close} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-md)', cursor: 'pointer', color: 'var(--neutral-400)' }}>✕</button>
      </div>

      {loadError ? (
        <div role="alert" style={{ fontSize: 'var(--font-size-sm)', color: '#991b1b' }}>Could not load this book's links: {loadError}</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 'var(--space-10)' }}>
            {rows.length === 0 && <span style={{ fontSize: 11.5, color: 'var(--neutral-400)', fontStyle: 'italic' }}>No buy links yet</span>}
            {rows.map(item => {
              const isColumn = item.source === 'column';
              if (isColumn) columnIndex += 1;
              const index = columnIndex;
              const link = item.link;
              // A link added or changed here is not saved yet, so nothing is said about readers until it is.
              const unsaved = isColumn && isNew(link);
              const statusText = unsaved ? LINK_WORDS.notSavedYet
                : item.status === 'visible' ? LINK_WORDS.readersSee
                : item.status === 'same' ? LINK_WORDS.sameAddress
                : LINK_WORDS.readersDont(item.reason || '');
              const options = BUY_STORES.includes(link.retailer) ? BUY_STORES : [...BUY_STORES, link.retailer];
              return (
                <div key={isColumn ? `c${index}` : `t${link.id}`} data-buy-link={item.source} data-buy-link-status={unsaved ? 'unsaved' : item.status}
                  style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', padding: '8px 10px' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {isColumn ? (
                      <>
                        <select aria-label="Store" value={link.retailer || ''} onChange={e => change(index, { retailer: e.target.value })} style={{ ...fieldStyle, flex: '0 0 auto' }}>
                          {options.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <input aria-label="Address" value={link.url || ''} onChange={e => change(index, { url: e.target.value })} style={{ ...fieldStyle, flex: '1 1 220px', minWidth: 0 }} />
                        <button type="button" onClick={() => takeOff(index)} disabled={busy} style={buttonStyle(false, busy)}>Take off</button>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', flex: '0 0 auto' }}>{link.retailer}</span>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', flex: '1 1 220px', minWidth: 0, overflowWrap: 'anywhere' }}>{link.url}</span>
                        <button type="button" onClick={() => removeFromTable(link)} disabled={busy} style={buttonStyle(false, busy)}>Remove</button>
                      </>
                    )}
                  </div>
                  <div style={{ marginTop: 5, fontSize: 'var(--font-size-xs)', lineHeight: 1.45, color: unsaved ? 'var(--neutral-600)' : item.status === 'visible' ? '#065f46' : 'var(--feedback-warning-ink)' }}>
                    {statusText}
                    {!isColumn && <span style={{ color: 'var(--neutral-400)' }}> · {LINK_WORDS.fromImport}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--space-10)' }}>
            <select aria-label="Store to add" value={store} onChange={e => setStore(e.target.value)} style={{ ...fieldStyle, flex: '0 0 auto' }}>
              {BUY_STORES.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <input aria-label="Address to add" value={address} onChange={e => setAddress(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
              placeholder="Address" style={{ ...fieldStyle, flex: '1 1 220px', minWidth: 0 }} />
            <button type="button" onClick={add} disabled={busy || !address.trim()} style={buttonStyle(false, busy || !address.trim())}>Add link</button>
          </div>

          {message && (
            <div role="status" data-buy-links-message={message.tone} style={{ marginBottom: 'var(--space-10)', fontSize: 'var(--font-size-sm)', lineHeight: 1.45, color: message.tone === 'ok' ? '#065f46' : '#991b1b' }}>
              {message.text}
            </div>
          )}

          <button type="button" onClick={save} disabled={busy || !dirty} style={buttonStyle(true, busy || !dirty)}>Save</button>
        </>
      )}
    </div>
  );
}
